import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  // const APIdomain = "http://localhost:5000";
  const APIdomain = "https://tst-edu-backend.vercel.app";

  const fetchUserData = async (userId) => {
    try {
      const response = await fetch(`${APIdomain}/api/users/${userId}`);
      const data = await response.json();

      if (data) {
        return data;
      } else {
        console.log("Failed to fetch user data");
      }
    } catch (err) {
      console.log("Error loading courses");
    }
  };

  // Load user data from localStorage on app start
  useEffect(() => {
    const appStart = async () => {
      const savedUser = localStorage.getItem("user");
      const savedToken = localStorage.getItem("token");

      if (savedUser && savedToken) {
        try {
          const oldUserData = JSON.parse(savedUser);
          const newUserData = await fetchUserData(oldUserData.id);
          setUser({
            ...newUserData,
            token: savedToken,
          });
        } catch (error) {
          console.error("Error parsing saved user data:", error);
          // Clear corrupted data
          localStorage.removeItem("user");
          localStorage.removeItem("token");
        }
      }
      setLoading(false);
    };
    appStart();
  }, []);

  // Redirect logic
  useEffect(() => {
    if (!loading && !user) {
      // List of public routes that don't require authentication
      const publicRoutes = ["/", "/home"];
      const isPublicRoute = publicRoutes.includes(
        location.pathname.toLowerCase()
      );

      if (!isPublicRoute) {
        navigate("/");
      }
    }
  }, [user, loading, navigate, location.pathname]);

  const login = (userData) => {
    const userWithToken = {
      id: userData.id,
      username: userData.username,
      email: userData.email,
      purchases: userData.purchases || [],
      type: userData.type,
      token: userData.token,
    };

    setUser(userWithToken);

    // Store in localStorage (excluding token from user object)
    const userForStorage = {
      id: userData.id,
      username: userData.username,
      email: userData.email,
      purchases: userData.purchases || [],
      type: userData.type,
    };

    localStorage.setItem("user", JSON.stringify(userForStorage));
    localStorage.setItem("token", userData.token);
    navigate("/home");
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    navigate("/");
  };

  // NEEDS Configuring
  const updateUser = (updatedData) => {
    const updatedUser = { ...user, ...updatedData };
    setUser(updatedUser);

    // Update localStorage
    const userForStorage = {
      id: updatedUser.id,
      username: updatedUser.username,
      email: updatedUser.email,
      purchases: updatedUser.purchases,
      type: updatedUser.type,
    };
    localStorage.setItem("user", JSON.stringify(userForStorage));
  };

  // NEEDS Configuring
  const addPurchase = (courseId) => {
    if (user) {
      const updatedPurchases = [...user.purchases, courseId];
      updateUser({ purchases: updatedPurchases });
    }
  };

  const isAuthenticated = !!user;

  const value = {
    // User data
    user,
    id: user?.id,
    username: user?.username,
    email: user?.email,
    purchases: user?.purchases || [],
    token: user?.token,
    type: user?.type,

    // Active API Domain
    APIdomain,

    // State
    isAuthenticated,
    loading,

    // Methods
    login,
    logout,
    updateUser,
    addPurchase,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
